import type { CompanyId, ClientId } from "../../../shared/kernel/identifiers.js";
import type { Company } from "../../../domain/identity/company.js";

export interface CompanyRepository {
  listCompanies(clientId: ClientId): Promise<Company[]>;
  getCompany(id: CompanyId): Promise<Company | null>;
  getCompanyByNormalizedCnpj(clientId: ClientId, normalizedCnpj: string): Promise<Company | null>;
  saveCompany(company: Company): Promise<void>;
}

export class InMemoryCompanyRepository implements CompanyRepository {
  private companies = new Map<string, Company>();

  constructor() {
    this.load();
  }

  private load(): void {
    try {
      const data = localStorage.getItem("inspect_companies");
      if (data) {
        this.companies = new Map(JSON.parse(data));
      }
    } catch (e) {
      console.warn("Failed to load companies from localStorage", e);
    }
  }

  private persist(): void {
    try {
      localStorage.setItem("inspect_companies", JSON.stringify(Array.from(this.companies.entries())));
    } catch (e) {
      console.warn("Failed to persist companies to localStorage", e);
    }
  }

  async listCompanies(clientId: ClientId): Promise<Company[]> {
    return Array.from(this.companies.values()).filter(c => c.clientId === clientId);
  }

  async getCompany(id: CompanyId): Promise<Company | null> {
    return this.companies.get(id) || null;
  }

  async getCompanyByNormalizedCnpj(clientId: ClientId, normalizedCnpj: string): Promise<Company | null> {
    return Array.from(this.companies.values()).find(
      c => c.clientId === clientId && c.normalizedCnpj === normalizedCnpj
    ) || null;
  }

  async saveCompany(company: Company): Promise<void> {
    this.companies.set(company.id, company);
    this.persist();
  }
}
